// Account, language, install, privacy, sign out and delete account. Opened from the avatar on Home.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError, clearSession } from '../api';
import { Link, navigate } from '../router';
import { Back } from '../icons';
import { useToast } from '../components/Shell';
import { applyLang, getLang, useT, type Lang } from '../i18n';
import { LANGUAGES, langAttr } from '../languages';
import { canInstall, install, isIOS, isStandalone } from '../install';
import { save, KEYS } from '../storage';
import type { Profile } from '../types';
import { startConnect, useProviders } from '../commerce';

export function wipeLocal() {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith('er.')).forEach((k) => localStorage.removeItem(k));
  } catch { /* storage unavailable */ }
  clearSession();
}


export function Settings() {
  const t = useT();
  const [me, setMe] = useState<Profile | null>(null);
  const [lang, setLang] = useState<Lang | ''>(getLang);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();
  const [apps, refreshApps] = useProviders();
  useEffect(() => { api.me().then((p) => { setMe(p); save(KEYS.me, p); }).catch(() => undefined); }, []);
  // Back from a provider's sign-in: say how it went, then tidy the URL.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get('connected') || q.get('provider');
    if (!id) return;
    const name = apps.find((a) => a.id === id)?.name ?? id;
    showToast(q.get('connected') ? t('settings.appConnected', { app: name }) : t('order.connectFailed', { app: name }));
    history.replaceState(null, '', '/settings');
  }, [apps.length]);
  const toggleApp = async (id: string, name: string, connected: boolean) => {
    try {
      if (!connected) { await startConnect(id, '/settings'); return; }
      await api.disconnect(id);
      refreshApps();
      showToast(t('settings.appDisconnected', { app: name }));
    } catch (e) { showToast((e as ApiError).message); }
  };

  const choose = async (l: Lang) => {
    const prev = lang;
    setLang(l);
    applyLang(l);
    try {
      await api.setLanguage(l);
      if (me) save(KEYS.me, { ...me, language: l });
      showToast(t('settings.saved'));
    } catch (e) {
      if (prev) { setLang(prev); applyLang(prev); }
      showToast((e as ApiError).message);
    }
  };
  const signOut = async () => {
    setBusy(true);
    await api.logout().catch(() => undefined);
    wipeLocal();
    navigate('/signin', { replace: true });
  };
  const deleteAccount = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    try {
      await api.deleteMe();
      wipeLocal();
      navigate('/signin', { replace: true });
    } catch (e) {
      showToast((e as ApiError).message);
      setBusy(false);
    }
  };

  return (
    <div class="page">
      <div class="backbar"><Link href="/" class="icon-btn" aria-label={t('common.back')}><Back /></Link></div>
      <div class="pad"><h1 class="h1" style="font-size:30px">{t('settings.title')}</h1></div>

      <section class="card" style="margin:16px 16px 0;padding:16px;display:flex;align-items:center;gap:12px">
        <span aria-hidden="true" style="width:52px;height:52px;border-radius:999px;background:var(--accent);color:var(--on-accent);display:flex;align-items:center;justify-content:center;font-family:var(--display);font-weight:800;font-size:22px">{me?.initial || '•'}</span>
        <div style="display:flex;flex-direction:column;min-width:0">
          <span style="font-weight:700;font-size:17px">{me?.name || t('settings.account')}</span>
          {me?.username && <span class="muted" style="font-size:14px">@{me.username} · {t('settings.via')}</span>}
        </div>
      </section>

      <section style="padding:22px 16px 0;display:flex;flex-direction:column;gap:8px">
        <h2 class="h2" style="font-size:18px;padding:0 4px">{t('settings.language')}</h2>
        <div class="tabs" role="group" aria-label={t('settings.language')}>
          {LANGUAGES.map((o) => <button aria-pressed={lang === o.lang} onClick={() => choose(o.lang)} lang={langAttr(o.lang)}>{o.name}</button>)}
        </div>
        <span class="small muted" style="padding:0 4px">{t('settings.languageHint')}</span>
      </section>

      {apps.length > 0 && (
        <section style="padding:22px 16px 0;display:flex;flex-direction:column;gap:8px">
          <h2 class="h2" style="font-size:18px;padding:0 4px">{t('settings.apps')}</h2>
          <span class="small muted" style="padding:0 4px">{t('settings.appsHint')}</span>
          <div class="rows">
            {apps.map((a) => (
              <div class="row">
                <span class="grow">
                  <span style="font-weight:700">{a.name}</span>
                  <span class="sub">{a.kind === 'grocery' ? t('settings.grocery') : t('settings.food')}{a.connected ? ` · ${t('settings.connected')}` : ''}</span>
                </span>
                {a.coming_soon && !a.connected
                  ? <span class="soon-badge">{t('soon.badge')}</span>
                  : <button class={a.connected ? 'btn-text' : 'btn btn-outline'} style="min-height:44px;padding:0 14px" onClick={() => toggleApp(a.id, a.name, a.connected)}>
                      {a.connected ? t('settings.disconnect') : t('settings.connect')}
                    </button>}
              </div>
            ))}
          </div>
        </section>
      )}

      {!isStandalone() && (canInstall() || isIOS()) && (
        <section class="card" style="margin:22px 16px 0;padding:16px;display:flex;flex-direction:column;gap:10px">
          <div style="font-weight:700">{t('settings.install')}</div>
          <div class="muted" style="font-size:14px">{canInstall() ? t('settings.installBody') : t('settings.installIOS')}</div>
          {canInstall() && <button class="btn btn-outline" onClick={() => install()}>{t('settings.installBtn')}</button>}
        </section>
      )}

      <section class="card" style="margin:22px 16px 0;padding:6px 16px;display:flex;flex-direction:column">
        <a href="https://utilhub.in/eat-recipe/privacy" target="_blank" rel="noopener" style="min-height:52px;display:flex;align-items:center;font-weight:600">{t('settings.privacy')}</a>
        <div style="height:1px;background:var(--line)" />
        <a href="https://utilhub.in/eat-recipe/data-deletion" target="_blank" rel="noopener" style="min-height:52px;display:flex;align-items:center;font-weight:600">{t('settings.deletion')}</a>
      </section>

      <section style="padding:22px 16px 32px;display:flex;flex-direction:column;gap:10px">
        <button class="btn btn-outline" disabled={busy} onClick={signOut}>{t('settings.signOut')}</button>
        <button class="btn-danger-text" disabled={busy} onClick={deleteAccount}>{confirmDelete ? t('settings.deleteConfirm') : t('settings.delete')}</button>
        <span class="small muted" style="text-align:center">{confirmDelete ? t('settings.deleteNote') : t('settings.deleteKeep')}</span>
      </section>
      {toast}
    </div>
  );
}
